<template>
    <div class="product-list group-list">
        <template v-for="(item, index) in productList" :key="item.id">
            <div
                v-if="item.type === 'group'"
                class="group-card"
                :class="{ 'group-card-active': item.isExpanded }"
                @click="handleProductClick(item)"
            >
                <div class="group-card-body">
                    <div class="group-card-name">{{ item.name }}</div>
                </div>
                <div class="group-card-arrow">
                  <span v-if="item.isExpanded">
                    <i class="fa fa-chevron-down"></i>
                  </span>
                  <span v-else>
                    <i class="fa fa-chevron-right"></i>
                  </span>
                </div>
            </div>

            <div
                v-else
                class="product-card"
                @click="onAddItem(item)"
            >
                <img
                    :src="item.image || '/app-assets/images/product-photo.png'"
                    class="product-img"
                />
                <div class="product-name">{{ item.name }}</div>
                <div class="product-meta">
                    <span>Stock: {{ item.stock_in_hand ?? 0 }}</span>
                    <span>MRP: {{ item.sales_price ?? 0 }}</span>
                </div>
            </div>

            <div
                v-if="item.type === 'group' && item.isExpanded"
                class="subgroup-dropdown"
            >
                <div v-if="item.isLoading" class="subgroup-loading">Loading...</div>
                <template v-for="child in (item.children || [])" :key="child.id">
                    <div
                        v-if="child.type === 'group'"
                        class="group-card group-card-nested"
                        :class="{ 'group-card-active': child.isExpanded }"
                        @click.stop="handleSubgroupChildClick(item, child)"
                    >
                        <div class="group-card-body">
                            <div class="group-card-name">{{ child.name }}</div>
                        </div>
                        <div class="group-card-arrow">
                           <span v-if="child.isExpanded">
                            <i class="fa fa-chevron-down"></i>
                          </span>
                          <span v-else>
                            <i class="fa fa-chevron-right"></i>
                          </span>
                        </div>
                    </div>

                    <div
                        v-else
                        class="product-card subgroup-child-card"
                        @click.stop="onAddItem(child)"
                    >
                        <img
                            :src="child.image || '/app-assets/images/product-photo.png'"
                            class="product-img"
                        />
                        <div class="product-name">{{ child.name }}</div>
                        <div class="product-meta">
                            <span>Stock: {{ child.stock_in_hand ?? 0 }}</span>
                            <span>MRP: {{ child.sales_price ?? 0 }}</span>
                        </div>
                    </div>

                    <div
                        v-if="child.type === 'group' && child.isExpanded"
                        class="subgroup-dropdown" style="margin-left: 16px;"
                    >
                        <div v-if="child.isLoading" class="subgroup-loading">Loading...</div>
                        <div
                            v-for="grandchild in (child.children || [])"
                            :key="grandchild.id"
                            class="product-card subgroup-child-card"
                            @click.stop="onAddItem(grandchild)"
                        >
                            <img
                                :src="grandchild.image || '/app-assets/images/product-photo.png'"
                                class="product-img"
                            />
                            <div class="product-name">{{ grandchild.name }}</div>
                            <div class="product-meta">
                                <span>Stock: {{ grandchild.stock_in_hand ?? 0 }}</span>
                                <span>MRP: {{ grandchild.sales_price ?? 0 }}</span>
                            </div>
                        </div>
                        <div v-if="!child.isLoading && (child.children || []).length === 0" class="subgroup-empty">
                            No products found
                        </div>
                    </div>

                </template>
                <div v-if="!item.isLoading && (item.children || []).length === 0" class="subgroup-empty">
                    No products found
                </div>
            </div>
        </template>
    </div>
</template>

<script setup>
import handleInventory from '@/services/modules/inventory';

const props = defineProps({
  productList: {
    type: Array,
    required: true,
  },
  companyId: {
    type: Number,
    default: null,
  },
  locationId: {
    type: Number,
    default: null,
  }
});

const emit = defineEmits(['addItem']);

const { fetchProductDetailsByParent } = handleInventory();

const handleProductClick = async (item) => {
  if (item.type !== 'group') {
    onAddItem(item);
    return;
  }

  item.isExpanded = !item.isExpanded;

  if (!item.isExpanded || item.children) {
    return;
  }

  item.isLoading = true;
  item.children = [];

  try {
    const query = `?company_id=${props.companyId}&location_id=${props.locationId}`;
    const res = await fetchProductDetailsByParent(query, item.id);

    item.children = res.status ? res.data : [];
  } catch (e) {
    item.children = [];
  } finally {
    item.isLoading = false;
  }
};

const handleSubgroupChildClick = async (parent, child) => {
  if (child.type !== 'group') {
    onAddItem(child);
    return;
  }

  child.isExpanded = !child.isExpanded;

  if (!child.isExpanded || child.children) {
    return;
  }

  child.isLoading = true;
  child.children = [];

  try {
    const query = `?company_id=${props.companyId}&location_id=${props.locationId}`;
    const res = await fetchProductDetailsByParent(query, child.id);

    child.children = res.status ? res.data : [];
  } catch (e) {
    child.children = [];
  } finally {
    child.isLoading = false;
  }
};

const onAddItem = (item) => {
  emit('addItem', item);
}
</script>

<style scoped>
.product-list {
    height: 620px;
    overflow-y: auto;
    padding: 6px;
}

/* ── GROUP / CATEGORY CARD ── */
.group-card {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    margin: 4px 2px;
    border-radius: 10px;
    cursor: pointer;
    background: linear-gradient(135deg, #e3f2fd, #bbdefb);
    border: 1px solid #90caf9;
    box-shadow: 0 2px 6px rgba(33, 150, 243, 0.15);
    transition: all 0.2s ease;
}

.group-card:hover {
    background: linear-gradient(135deg, #bbdefb, #90caf9);
    box-shadow: 0 4px 12px rgba(33, 150, 243, 0.3);
    transform: translateY(-1px);
}

.group-card-active {
    background: linear-gradient(135deg, #1976d2, #1565c0) !important;
    color: #fff !important;
    border-color: #1565c0 !important;
    box-shadow: 0 4px 14px rgba(21, 101, 192, 0.4) !important;
}

.group-card-active .group-card-sub {
    color: rgba(255,255,255,0.75) !important;
}

.group-card-active .group-card-arrow {
    color: rgba(255,255,255,0.9) !important;
}

.group-card-nested {
    background: linear-gradient(135deg, #f3e5f5, #e1bee7);
    border-color: #ce93d8;
    box-shadow: 0 2px 6px rgba(156, 39, 176, 0.15);
}

.group-card-nested:hover {
    background: linear-gradient(135deg, #e1bee7, #ce93d8);
}

.group-card-nested.group-card-active {
    background: linear-gradient(135deg, #7b1fa2, #6a1b9a) !important;
    border-color: #6a1b9a !important;
}

.group-card-icon {
    font-size: 22px;
    line-height: 1;
    flex-shrink: 0;
}

.group-card-body {
    flex: 1;
    min-width: 0;
}

.group-card-name {
    font-weight: 700;
    font-size: 13px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.group-card-sub {
    font-size: 10px;
    color: #5e7ea0;
    margin-top: 1px;
}

.group-card-arrow {
    font-size: 11px;
    color: #1976d2;
    flex-shrink: 0;
}

/* ── OLD SUBGROUP ALIASES (keep for safety) ── */
.subgroup-arrow {
    font-size: 10px;
    margin-left: 4px;
    color: #0097a7;
}

.subgroup-dropdown {
    margin: 0 4px 4px 12px;
    border-left: 2px solid #b2ebf2;
    padding-left: 6px;
    animation: slideDown 0.2s ease;
}

@keyframes slideDown {
    from { opacity: 0; transform: translateY(-4px); }
    to   { opacity: 1; transform: translateY(0); }
}

.subgroup-child-card {
    background: #f9fffe !important;
    border: 1px dashed #b2ebf2;
    margin: 3px 0 !important;
}

.subgroup-child-card:hover {
    background: #e0f7fa !important;
}

.subgroup-nested-card {
    background: #fff9e6 !important;
    border: 1px solid #ffe082 !important;
    margin: 3px 0 !important;
}

.subgroup-nested-card:hover {
    background: #fff1c1 !important;
}

.subgroup-loading {
    font-size: 12px;
    color: #888;
    padding: 8px 4px;
    text-align: center;
}

.subgroup-empty {
    font-size: 12px;
    color: #aaa;
    padding: 8px 4px;
    text-align: center;
    font-style: italic;
}

/* PRODUCT CARD */
.product-card {
    min-height: 80px;
    padding: 6px;
    cursor: pointer;
    background: #fff;

    display: grid;
    grid-template-columns: 50px 1fr;
    grid-template-rows: auto auto;
    column-gap: 6px;
    border-radius: 12px;
    padding: 12px;
    margin: 3px;
    transition: all 0.2s ease;

    /* NO BORDER */
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.4);
}

.product-card:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
    transform: translateY(-2px);
    background: #f3f2f7;
}

/* IMAGE */
.product-img {
    width: 48px;
    height: 48px;
    object-fit: cover;
    grid-row: 1 / span 2;
}

/* NAME */
.product-name {
    font-weight: 620;
    font-size: 13px;

    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

/* MRP + STOCK */
.product-meta {
    font-size: 11px;
    display: flex;
    justify-content: space-between;
}
</style>
